import { useState, useCallback, useRef, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Building2, Search } from 'lucide-react';

import { MasterPageHeader }   from '@/components/master/MasterPageHeader';
import { MasterLookupBar, MasterCodeLookup } from '@/components/master/MasterLookupBar';
import { MasterFlash }         from '@/components/master/MasterFlash';
import { MasterAuditTab }      from '@/components/master/MasterAuditTab';
import { MasterConfirmDialogs } from '@/components/master/MasterConfirmDialogs';
import { MasterToolbar }       from '@/components/master/MasterToolbar';
import { MasterSearchModal }   from '@/components/master/MasterSearchModal';
import type { SearchColumn }   from '@/components/master/MasterSearchModal';
import { MasterReportPreview } from '@/components/master/report/MasterReportPreview';
import { ReportHeader }        from '@/components/master/report/ReportHeader';
import { ReportSection }       from '@/components/master/report/ReportSection';
import { ReportFooter }        from '@/components/master/report/ReportFooter';
import {
  Badge, Card, Field, IconButton, Input, Tabs, Toggle, controlBase,
} from '@/components/ui';

import { useAuth }             from '@/hooks/useAuth';
import { usePermission }       from '@/hooks/usePermission';
import { useFlashMessage }     from '@/hooks/useFlashMessage';
import { useMasterScreenState } from '@/hooks/useMasterScreenState';
import { useMasterRecord, masterListFetcher } from '@/hooks/useMasterRecord';
import { Permissions }         from '@/constants/permissions';
import { bankApi, bankBranchApi } from '@/api/bank.api';
import type { BankBranchDto, BankBranchListDto, BankListDto } from '@/types/bank.types';
import { cn } from '@/utils/cn';
import { formatDateTime } from '@/utils/format';

// ── Zod schema ─────────────────────────────────────────────────────────────────

const schema = z.object({
  bankId:       z.string().uuid('Bank is required'),
  branchCode:   z.string().min(1, 'Branch code is required').max(20),
  branchName:   z.string().min(1, 'Branch name is required').max(200),
  addressLine1: z.string().max(200).nullable().optional(),
  addressLine2: z.string().max(200).nullable().optional(),
  city:         z.string().max(100).nullable().optional(),
  phone:        z.string().max(50).nullable().optional(),
  email:        z.string().email().nullable().optional().or(z.literal('')),
  isActive:     z.boolean(),
});

type FormValues = z.infer<typeof schema>;

const EMPTY_FORM: FormValues = {
  bankId: '', branchCode: '', branchName: '', addressLine1: null, addressLine2: null,
  city: null, phone: null, email: null, isActive: true,
};

const TABS = ['General', 'Contact', 'Audit'] as const;
type Tab = (typeof TABS)[number];

const BRANCH_SEARCH_COLUMNS: SearchColumn<BankBranchListDto>[] = [
  { key: 'branchCode', label: 'Code',   className: 'w-24 font-mono' },
  { key: 'branchName', label: 'Name' },
  { key: 'city',       label: 'City',   className: 'w-24', render: r => r.city ?? '—' },
  { key: 'isActive',   label: 'Status', className: 'w-20',
    render: r => <Badge variant={r.isActive ? 'success' : 'default'}>{r.isActive ? 'Active' : 'Inactive'}</Badge> },
];

const BANK_SEARCH_COLUMNS: SearchColumn<BankListDto>[] = [
  { key: 'bankCode', label: 'Code',    className: 'w-20 font-mono' },
  { key: 'bankName', label: 'Name' },
  { key: 'country',  label: 'Country', className: 'w-24', render: r => r.country ?? '—' },
];

function dtoToForm(dto: BankBranchDto): FormValues {
  return {
    bankId: dto.bankId, branchCode: dto.branchCode, branchName: dto.branchName,
    addressLine1: dto.addressLine1 ?? null, addressLine2: dto.addressLine2 ?? null,
    city: dto.city ?? null, phone: dto.phone ?? null, email: dto.email ?? null,
    isActive: dto.isActive,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

export const BankBranchPage = () => {
  const { user }          = useAuth();
  const { hasPermission } = usePermission();

  const canView   = hasPermission(Permissions.MST_BANK_BRANCH_VIEW);
  const canCreate = hasPermission(Permissions.MST_BANK_BRANCH_CREATE);
  const canEdit   = hasPermission(Permissions.MST_BANK_BRANCH_EDIT);
  const canDelete = hasPermission(Permissions.MST_BANK_BRANCH_DELETE);

  const screen = useMasterScreenState({ canEdit, canDelete });

  const { flashMsg, flash } = useFlashMessage();
  const branchInputRef = useRef<HTMLInputElement>(null);

  const [, setBankCodeInput]   = useState('');
  const [branchCodeInput, setBranchCodeInput] = useState('');
  const [bankSearchOpen,  setBankSearchOpen]  = useState(false);

  // Selected bank display info
  const [selectedBank, setSelectedBank] = useState<{ id: string; code: string; name: string } | null>(null);

  const [showNewConfirm,    setShowNewConfirm]    = useState<string | null>(null);

  const {
    register, handleSubmit, control, reset, watch,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY_FORM });

  const isActive = watch('isActive');

  const master = useMasterRecord<BankBranchDto, Tab, FormValues>({
    screen, flash, firstTab: 'General', focusRef: branchInputRef,
    clearForm:   () => reset(EMPTY_FORM),
    clearScreen: () => { setBankCodeInput(''); setBranchCodeInput(''); setSelectedBank(null); },
    onDelete: {
      remove:   id => bankBranchApi.remove(id),
      describe: r => `Branch '${r.branchCode}'`,
    },
  });

  const {
    record, setRecord, activeTab, setActiveTab, saving, setSaving, lookupBusy, setLookupBusy,
    printOpen, setPrintOpen, searchOpen, setSearchOpen,
    pendingSave, setPendingSave, showSaveConfirm, setShowSaveConfirm,
    showDeleteConfirm, setShowDeleteConfirm, resetToInitial, executeDelete, beginSave,
  } = master;


  const loadRecord = useCallback((dto: BankBranchDto) => {
    setRecord(dto); reset(dtoToForm(dto));
    setBranchCodeInput(dto.branchCode);
    setSelectedBank(dto.bankId ? { id: dto.bankId, code: '', name: dto.bankName ?? '' } : null);
    screen.setMode('view'); setActiveTab('General');
  }, [reset, screen]);

  const handleBranchLookup = async () => {
    const branchCode = branchCodeInput.trim().toUpperCase();
    if (!branchCode) return;
    if (!selectedBank) { flash('error', 'Please select a bank first.'); return; }
    if (!canView) { flash('error', 'You do not have permission to view bank branches.'); return; }
    setLookupBusy(true);
    try {
      const res = await bankBranchApi.getByCode(selectedBank.id, branchCode);
      if (res.data.data) loadRecord(res.data.data);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 404) {
        if (!canCreate) { flash('error', `Branch '${branchCode}' was not found for the selected bank.`); return; }
        setShowNewConfirm(branchCode);
      } else { flash('error', 'Lookup failed. Please try again.'); }
    } finally { setLookupBusy(false); }
  };

  const handleBankSelect = (item: BankListDto) => {
    setBankSearchOpen(false);
    setBankCodeInput(item.bankCode);
    setSelectedBank({ id: item.id, code: item.bankCode, name: item.bankName });
    setTimeout(() => branchInputRef.current?.focus(), 50);
  };

  const handleConfirmCreateNew = () => {
    const code = showNewConfirm; setShowNewConfirm(null); if (!code) return;
    reset({ ...EMPTY_FORM, bankId: selectedBank?.id ?? '', branchCode: code });
    setBranchCodeInput(code);
    setRecord(null); screen.setMode('new'); setActiveTab('General');
  };

  const handleSaveClick = () => {
    // The second argument runs when the form is invalid. Without it a failed validation did
    // nothing at all -- no save, no message, no focus -- so the button looked broken rather than
    // the form looking wrong.
    handleSubmit(beginSave, (formErrors) => {
      const first = Object.values(formErrors)[0]?.message;
      flash('error', first ? String(first) : 'Please correct the highlighted fields.');
    })();
  };

  const executeSave = async () => {
    if (!pendingSave) return;
    setShowSaveConfirm(false); setSaving(true);
    const n = (v: string | null | undefined) => v?.trim() || null;
    try {
      const d = pendingSave;
      if (screen.isNew) {
        await bankBranchApi.create({
          bankId: d.bankId, branchCode: d.branchCode.trim().toUpperCase(),
          branchName: d.branchName.trim(),
          addressLine1: n(d.addressLine1), addressLine2: n(d.addressLine2),
          city: n(d.city), phone: n(d.phone), email: n(d.email),
        });
        flash('success', 'Bank branch created successfully.');
        resetToInitial();
      } else if (screen.isEdit && record) {
        await bankBranchApi.update(record.id, {
          branchName: d.branchName.trim(),
          addressLine1: n(d.addressLine1), addressLine2: n(d.addressLine2),
          city: n(d.city), phone: n(d.phone), email: n(d.email),
          isActive: d.isActive,
        });
        flash('success', 'Bank branch updated successfully.');
        resetToInitial();
      }
    } catch (err: unknown) {
      const body = (err as { response?: { data?: { message?: string;
                       errors?: { message?: string }[] } } }).response?.data;
      // errors[0] names the field. The envelope's own message is just "Validation failed."
      const msg = body?.errors?.[0]?.message ?? body?.message ?? 'Save failed.';
      flash('error', msg);
    } finally { setSaving(false); setPendingSave(null); }
  };


  const fetchBranches = useCallback(async (search: string, page: number, pageSize: number) => {
    const res = await bankBranchApi.getList({
      bankId: selectedBank?.id, search, pageNumber: page, pageSize,
    });
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  }, [selectedBank]);

  const fetchBanks = useMemo(() => masterListFetcher(bankApi.getList), []);

  const handleSearchSelect = (item: BankBranchListDto) => {
    setSearchOpen(false); setLookupBusy(true);
    bankBranchApi.getById(item.id)
      .then(res => { if (res.data.data) loadRecord(res.data.data); })
      .catch(() => flash('error', 'Failed to load branch.'))
      .finally(() => setLookupBusy(false));
  };

  const fieldsDisabled = screen.isInitial || screen.isView;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full min-h-0">
      <MasterPageHeader icon={Building2} title="Bank Branch Maintenance"
        subtitle="Manage bank branches and contact details" screen={screen} />

      <MasterToolbar
        {...screen.toolbar} {...master.toolbar}
        saving={saving} loading={lookupBusy} onSave={handleSaveClick}
      />

      <MasterFlash message={flashMsg} />

      <MasterLookupBar
        label="Branch Code"
        summary={record && (<>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{record.branchName}</span>
          {!record.isActive && (
            <span className="px-1.5 py-0.5 rounded bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 text-[10px] font-bold">Inactive</span>
          )}
        </>)}
        hint={screen.isInitial ? "Pick the bank, then enter a branch code and press Enter." : undefined}
      >
        {/* A branch code is only unique within its bank, so the bank is chosen first. */}
        <div className={cn(
          'flex items-center h-8 rounded-md border px-2 min-w-36',
          'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600',
        )}>
          <span className="font-mono text-xs text-gray-700 dark:text-gray-300 flex-1">
            {selectedBank ? `${selectedBank.code} — ${selectedBank.name}` : 'No bank selected'}
          </span>
        </div>
        <IconButton
          label="Select bank"
          icon={<Search size={14} />}
          onClick={() => setBankSearchOpen(true)}
          disabled={screen.isNew || screen.isEdit || saving}
        />
        <MasterCodeLookup
          inputRef={branchInputRef}
          value={branchCodeInput}
          onChange={setBranchCodeInput}
          onSubmit={handleBranchLookup}
          onBrowse={() => setSearchOpen(true)}
          browseLabel="Search branches"
          placeholder="Code + Enter…"
          disabled={screen.isNew || screen.isEdit || saving}
          busy={lookupBusy}
        />
      </MasterLookupBar>

      <div className="flex-1 overflow-auto">
        <form onSubmit={e => e.preventDefault()} noValidate>
          <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

          <div className="p-5 space-y-5 max-w-2xl">
            {activeTab === 'General' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">Branch Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Hidden bankId */}
                  <input type="hidden" {...register('bankId')} />

                  {/* Bank display */}
                  <Field label="Bank" className="sm:col-span-2">
                    <div className={cn(controlBase(), 'h-9 px-3 flex items-center cursor-not-allowed')}>
                      <span className="text-gray-600 dark:text-gray-400 text-sm">
                        {selectedBank
                          ? `${selectedBank.code} — ${selectedBank.name}`
                          : record?.bankName ?? 'No bank selected'}
                      </span>
                    </div>
                  </Field>

                  <Input label="Branch Code" {...register('branchCode')} disabled
                    placeholder="— enter via lookup bar above —" className="font-mono uppercase" />
                  <Input label="Branch Name *" {...register('branchName')} error={errors.branchName?.message}
                    disabled={fieldsDisabled} placeholder="e.g. Colombo City Branch" />

                  {(screen.isEdit || screen.isView) && (
                    <div className="sm:col-span-2">
                      <Controller name="isActive" control={control} render={({ field }) => (
                        <Toggle
                          label={isActive ? 'Active' : 'Inactive'}
                          checked={field.value}
                          onChange={field.onChange}
                          disabled={fieldsDisabled}
                        />
                      )} />
                    </div>
                  )}
                </div>
              </Card>
            )}

            {activeTab === 'Contact' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">Contact Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input label="Address Line 1" {...register('addressLine1')} disabled={fieldsDisabled}
                    maxLength={200} placeholder="Street / Unit" fieldClassName="sm:col-span-2" />
                  <Input label="Address Line 2" {...register('addressLine2')} disabled={fieldsDisabled}
                    maxLength={200} fieldClassName="sm:col-span-2" />
                  <Input label="City" {...register('city')} disabled={fieldsDisabled} />
                  <Input label="Phone" {...register('phone')} disabled={fieldsDisabled} />
                  <Input label="Email" {...register('email')} type="email" disabled={fieldsDisabled}
                    error={errors.email?.message} />
                </div>
              </Card>
            )}

            {activeTab === 'Audit' && <MasterAuditTab record={record} />}
          </div>
        </form>
      </div>

      {/* Modals */}
      <MasterSearchModal<BankListDto>
        isOpen={bankSearchOpen} onClose={() => setBankSearchOpen(false)}
        title="Select Bank" columns={BANK_SEARCH_COLUMNS}
        fetchData={fetchBanks} onSelect={handleBankSelect}
      />

      <MasterSearchModal<BankBranchListDto>
        isOpen={searchOpen} onClose={() => setSearchOpen(false)}
        title="Browse Bank Branches" columns={BRANCH_SEARCH_COLUMNS}
        fetchData={fetchBranches} onSelect={handleSearchSelect}
      />

      <MasterConfirmDialogs
        entity="Bank branch"
        screen={screen}
        record={record}
        describe={r => `${r.branchCode} — ${r.branchName}`}
        saving={saving}
        create={{
          label: showNewConfirm,
          onConfirm: handleConfirmCreateNew,
          onCancel: () => setShowNewConfirm(null),
        }}
        saveOpen={showSaveConfirm}
        onSaveConfirm={executeSave}
        onSaveCancel={() => { setShowSaveConfirm(false); setPendingSave(null); }}
        deleteOpen={showDeleteConfirm}
        onDeleteConfirm={executeDelete}
        onDeleteCancel={() => setShowDeleteConfirm(false)}
      />

      {record && (
        <MasterReportPreview isOpen={printOpen} onClose={() => setPrintOpen(false)} title="Bank Branch Report">
          <ReportHeader reportTitle="Bank Branch Master Report"
            printedBy={user?.fullName ?? user?.username} printedAt={new Date()}
            meta={[{ label: 'Branch Code', value: record.branchCode }]}
          />
          <ReportSection title="Branch Details" fields={[
            { label: 'Branch Code', value: record.branchCode },
            { label: 'Branch Name', value: record.branchName, wide: true },
            { label: 'Bank',        value: record.bankName },
            { label: 'Status',      value: record.isActive, display: record.isActive ? 'Active' : 'Inactive' },
          ]} />
          <ReportSection title="Contact" fields={[
            { label: 'Address Line 1', value: record.addressLine1, wide: true },
            { label: 'Address Line 2', value: record.addressLine2, wide: true },
            { label: 'City',           value: record.city },
            { label: 'Phone',          value: record.phone },
            { label: 'Email',          value: record.email },
          ]} />
          <ReportSection title="Audit Information" fields={[
            { label: 'Created By',    value: record.createdByName ?? record.createdBy },
            { label: 'Created Date',  value: formatDateTime(record.createdDate) },
            { label: 'Modified By',   value: record.modifiedByName ?? record.modifiedBy },
            { label: 'Modified Date', value: formatDateTime(record.modifiedDate) },
          ]} />
          <ReportFooter />
        </MasterReportPreview>
      )}
    </div>
  );
};
